<template #default>
  <div class="page-view">
    <a-card class="page-filter" :bordered="false">
      <a-form
        layout="horizontal"
        :wrapper-col="wrapperCol"
        @submit.prevent="onSearch"
      >
        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item
              label="Name"
              :labelCol="{ span: 2, style: { textAlign: 'left' } }"
              :wrapperCol="{ span: 22 }"
            >
              <a-input
                v-model:value="query.name"
                placeholder="Search by issuer name..."
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              :colon="false"
              :labelCol="{ span: 4, style: { textAlign: 'left' } }"
              :wrapperCol="{ span: 20 }"
            >
              <div class="page-actions">
                <a-button type="primary" @click="onSearch" :loading="loading"
                  >Search</a-button
                >
                <a-button @click="showAddModal">
                  <PlusOutlined />
                  Add
                </a-button>
              </div>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div class="page-grid page-grid--h370">
      <AgGridVue
        style="width: 100%; height: 100%"
        :theme="themeBalham"
        :loading="loading"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
      />
    </div>

    <AddEditIssuerView
      v-model:visible="addModalVisible"
      :isEdit="isEdit"
      :addForm="addForm"
      @update:issuer="handleUpdateIssuer"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { themeBalham } from 'ag-grid-community'
import { message } from 'ant-design-vue'
import { PlusOutlined } from '@ant-design/icons-vue'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import AddEditIssuerView from './AddEditIssuerView.vue'
import issuerApi from '@/api/issuer'

const gridApi = ref(null)
const gridOptions = ref({
  rowSelection: 'single',
  onGridReady: (params) => {
    gridApi.value = params.api
  },
})

const defaultColDef = { filter: true, sortable: true, resizable: true }

const columnDefs = [
  { headerName: 'Name', field: 'name', width: 300 },
  {
    headerName: 'Desc',
    field: 'desc',
    tooltipField: 'desc',
    width: 160,
  },
  { headerName: 'Domicile', field: 'countryOfDomicile', width: 160 },
  { headerName: 'Nationality', field: 'countryOfNationality', width: 160 },
  { headerName: 'Risk', field: 'countryOfRisk', width: 160 },
  { headerName: 'Industry', field: 'industry', width: 200 },
  {
    headerName: 'GicsIndustry',
    field: 'gicsIndustry',
    tooltipField: 'gicsIndustry',
    width: 200,
  },
  {
    headerName: 'GicsSubIndustry',
    field: 'gicsSubIndustry',
    tooltipField: 'gicsSubIndustry',
    width: 200,
  },
  { headerName: 'Status', field: 'status', width: 100 },
  {
    headerName: 'Edit',
    field: 'edit',
    cellRenderer: AgEditCellRenderer,
    width: 120,
    cellRendererParams: {
      editButton: 'Edit',
      showEditModal: (data) => showEditModal(data),
    },
  },
]

const wrapperCol = { span: 24 }
const loading = ref(false)
const rowData = ref([])
const addModalVisible = ref(false)
const addForm = ref({})
const isEdit = ref(false)
const query = ref({ name: null })

const onSearch = async () => {
  loading.value = true
  try {
    const name = query.value.name ? String(query.value.name).trim() : ''
    const resp = await issuerApi.getIssuers(name)
    rowData.value = Array.isArray(resp.data)
      ? resp.data
      : resp.data?.issuers || []
    if (!rowData.value || rowData.value.length === 0)
      message.info('No data found.')
  } catch (e) {
    message.error(e?.response?.data?.message || 'Query failed')
  } finally {
    loading.value = false
  }
}

const showAddModal = () => {
  isEdit.value = false
  addForm.value = {
    issuerId: null,
    externalIssuerId: null,
    name: null,
    shortName: null,
    desc: null,
    industry: null,
    gicsSubIndustry: null,
    countryOfDomicile: null,
    countryOfNationality: null,
    countryOfRisk: null,
    status: 'A',
  }
  addModalVisible.value = true
}

const showEditModal = (event) => {
  const data = event?.data || event
  isEdit.value = true
  addForm.value = { ...data }
  addModalVisible.value = true
}

const handleUpdateIssuer = async (payload, editFlag) => {
  loading.value = true
  try {
    if (editFlag) {
      await issuerApi.updateIssuer(payload)
      message.success('Issuer updated')
    } else {
      await issuerApi.createIssuer(payload)
      message.success('Issuer created')
    }
    addModalVisible.value = false
    await onSearch()
  } catch (e) {
    message.error(e?.response?.data?.message || 'Failed')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // onSearch()
})
</script>
